import { getAuthMode, getBaseApiUrl } from '@/config/runtime'
import { getAuthToken } from '@/services/hankoAuth'

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>
  /**
   * The caller expects a 401/403 as a regular answer (a surface gated on a
   * credential the session may not carry, e.g. Hanko-only node control or the
   * instance super-admin gate). The error is still thrown, but the session is
   * not invalidated.
   */
  expectAuthRefusal?: boolean
}

interface RetryConfig {
  maxRetries: number
  backoffMultiplier: number
  initialDelayMs: number
  retryableStatusCodes: number[]
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  backoffMultiplier: 2,
  initialDelayMs: 1000,
  retryableStatusCodes: [429, 503],
}

export const LOCAL_AUTH_INVALIDATED_EVENT = 'agent-maurice:local-auth-invalidated'

export const invalidateLocalSession = (status: number) => {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_user_id')
  localStorage.removeItem('auth_email')

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(LOCAL_AUTH_INVALIDATED_EVENT, {
        detail: { status },
      }),
    )
  }
}

export async function getAPIAuthToken(): Promise<string | null> {
  const authMode = getAuthMode()

  if (authMode === 'hanko') {
    const token = await getAuthToken()
    if (token) {
      localStorage.setItem('auth_token', token)
      return token
    }

    localStorage.removeItem('auth_token')
    return null
  }

  const token = localStorage.getItem('auth_token')
  if (token) {
    localStorage.setItem('auth_token', token)
  }

  return token
}

export function handleAPIAuthFailure(status: number) {
  if (getAuthMode() === 'local' && (status === 401 || status === 403)) {
    invalidateLocalSession(status)
    return
  }

  if (getAuthMode() === 'hanko' && (status === 401 || status === 403)) {
    localStorage.removeItem('auth_token')
  }
}

// Carries the HTTP status and raw (untrusted) response body alongside the
// error message, so callers can qualify the failure (timeout, service error,
// network) instead of pattern-matching the message string.
export class ApiRequestError extends Error {
  readonly status: number
  readonly bodyText: string

  constructor(status: number, bodyText: string, message: string) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.bodyText = bodyText
  }
}

class APIClient {
  private readonly fallbackBaseURL: string
  private readonly retryConfig: RetryConfig

  constructor(baseURL: string = '/api', retryConfig?: Partial<RetryConfig>) {
    this.fallbackBaseURL = baseURL
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig }
  }

  private get baseURL(): string {
    return getBaseApiUrl() || this.fallbackBaseURL
  }

  private buildURL(endpoint: string): string {
    const baseURL = this.baseURL.replace(/\/+$/, '')
    if (endpoint === '/v2' || endpoint.startsWith('/v2/')) {
      const suffix = endpoint.slice('/v2'.length)
      if (/\/v1$/i.test(baseURL)) {
        return `${baseURL.replace(/\/v1$/i, '/v2')}${suffix}`
      }
      if (/\/api$/i.test(baseURL)) {
        return `${baseURL}/v2${suffix}`
      }
    }
    return `${baseURL}${endpoint}`
  }

  private getRetryDelay(attemptNumber: number, retryAfterHeader?: string): number {
    // If Retry-After header is present, use it (expecting seconds)
    if (retryAfterHeader) {
      const retryAfter = parseInt(retryAfterHeader, 10)
      if (!isNaN(retryAfter)) {
        return retryAfter * 1000
      }
    }

    // Exponential backoff: 1s, 2s, 4s, ...
    return (
      this.retryConfig.initialDelayMs *
      Math.pow(this.retryConfig.backoffMultiplier, attemptNumber - 1)
    )
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private parseResponseBody<T>(body: string): T | undefined {
    const trimmedBody = body.trim()
    if (!trimmedBody) {
      return undefined
    }

    if (
      trimmedBody.startsWith('{') ||
      trimmedBody.startsWith('[') ||
      trimmedBody === 'null'
    ) {
      return JSON.parse(trimmedBody) as T
    }

    return undefined
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { params, expectAuthRefusal, ...fetchOptions } = options

    let url = this.buildURL(endpoint)

    if (params) {
      const queryString = new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      ).toString()
      url += `?${queryString}`
    }

    let lastError: Error | null = null
    let attempts = 0

    while (attempts < this.retryConfig.maxRetries) {
      attempts++

      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        }

        const token = await getAPIAuthToken()

        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const response = await fetch(url, {
          ...fetchOptions,
          headers,
        })

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '')
          const details = errorBody || response.statusText || 'Unknown API error'
          const errorMessage = `API Error ${response.status}: ${details}`

          // Check if this is a retryable error and we have retries left
          if (
            this.retryConfig.retryableStatusCodes.includes(response.status) &&
            attempts < this.retryConfig.maxRetries
          ) {
            lastError = new ApiRequestError(response.status, errorBody, errorMessage)
            const retryAfter = response.headers.get('Retry-After')
            const delayMs = this.getRetryDelay(attempts, retryAfter || undefined)
            await this.sleep(delayMs)
            continue // Retry
          }

          if (!expectAuthRefusal) {
            handleAPIAuthFailure(response.status)
          }
          throw new ApiRequestError(response.status, errorBody, errorMessage)
        }

        if (response.status === 204) {
          return undefined as T
        }

        const contentType = response.headers.get('content-type') || ''
        const responseBody = await response.text()

        if (!responseBody.trim()) {
          return undefined as T
        }

        if (contentType.includes('application/json')) {
          return JSON.parse(responseBody) as T
        }

        const parsedBody = this.parseResponseBody<T>(responseBody)
        if (parsedBody !== undefined) {
          return parsedBody
        }

        return undefined as T
      } catch (error) {
        // If it's not a network error and not a retryable status, throw immediately
        if (error instanceof Error && error.message.startsWith('API Error')) {
          throw error
        }

        // Network errors or other issues might be retryable
        lastError = error instanceof Error ? error : new Error(String(error))
        if (attempts < this.retryConfig.maxRetries) {
          const delayMs = this.getRetryDelay(attempts)
          await this.sleep(delayMs)
          continue
        }
      }
    }

    // All retries exhausted
    if (lastError) {
      throw lastError
    }

    throw new Error('Request failed after all retries')
  }

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }

  patch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }
}

export const apiClient = new APIClient()
